import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { identifyUser, resetPostHog } from "@/lib/posthog";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";

// IQ Assistant standalone — no firms, no MFA, no impersonation, no roles.
// Tenancy is workspace-scoped; each user has at least one workspace via the
// handle_new_user trigger.

export type WorkspaceRole = "owner" | "admin" | "member";

interface Profile {
  id: string;                       // = auth.users.id
  display_name: string | null;
  avatar_url: string | null;
  default_workspace_id: string | null;
}

interface WorkspaceMembership {
  workspace_id: string;
  role: WorkspaceRole;
  workspace_name: string;
  workspace_kind: "solo" | "team";
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  workspaces: WorkspaceMembership[];
  currentWorkspaceId: string | null;
  setCurrentWorkspaceId: (id: string) => void;
  isLoading: boolean;
  signUp: (email: string, password: string, displayName: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  // Stub kept for backwards compat with components that still call hasRole.
  // In assistant land "roles" are workspace memberships, not CRM AppRoles.
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const CURRENT_WORKSPACE_KEY = "iq_current_workspace_id";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceMembership[]>([]);
  const [currentWorkspaceId, _setCurrentWorkspaceId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const setCurrentWorkspaceId = (id: string) => {
    _setCurrentWorkspaceId(id);
    try { localStorage.setItem(CURRENT_WORKSPACE_KEY, id); } catch { /* noop */ }
  };

  const hydrate = async (uid: string) => {
    const [{ data: prof }, { data: members }] = await Promise.all([
      supabase.from("profiles")
        .select("id, display_name, avatar_url, default_workspace_id")
        .eq("id", uid)
        .maybeSingle(),
      supabase.from("workspace_members")
        .select("workspace_id, role, workspaces:workspace_id(name, kind)")
        .eq("user_id", uid),
    ]);

    setProfile(prof ?? null);

    const memberships: WorkspaceMembership[] = (members ?? []).map((m: any) => ({
      workspace_id: m.workspace_id,
      role: m.role,
      workspace_name: m.workspaces?.name ?? "Workspace",
      workspace_kind: m.workspaces?.kind ?? "solo",
    }));
    setWorkspaces(memberships);

    // Pick current workspace: localStorage > profile.default > first membership.
    let target: string | null = null;
    try { target = localStorage.getItem(CURRENT_WORKSPACE_KEY); } catch { /* noop */ }
    if (!target || !memberships.some((m) => m.workspace_id === target)) {
      target = prof?.default_workspace_id ?? memberships[0]?.workspace_id ?? null;
    }
    _setCurrentWorkspaceId(target);
  };

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      if (cancelled) return;
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        await hydrate(s.user.id);
        identifyUser(s.user.id, { email: s.user.email });
        setSentryUser({ id: s.user.id, email: s.user.email });
      }
      setIsLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, s) => {
      if (cancelled) return;
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        await hydrate(s.user.id);
        identifyUser(s.user.id, { email: s.user.email });
        setSentryUser({ id: s.user.id, email: s.user.email });
      } else {
        setProfile(null);
        setWorkspaces([]);
        _setCurrentWorkspaceId(null);
        resetPostHog();
        clearSentryUser();
        try { localStorage.removeItem(CURRENT_WORKSPACE_KEY); } catch { /* noop */ }
      }
    });

    return () => { cancelled = true; sub.subscription.unsubscribe(); };
  }, []);

  const signUp: AuthContextType["signUp"] = async (email, password, displayName) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { display_name: displayName } },
    });
    return { error: error ?? null };
  };

  const signIn: AuthContextType["signIn"] = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const hasRole = (role: string) => {
    if (!currentWorkspaceId) return false;
    const m = workspaces.find((w) => w.workspace_id === currentWorkspaceId);
    if (!m) return false;
    if (role === "owner") return m.role === "owner";
    if (role === "admin") return m.role === "owner" || m.role === "admin";
    return true;
  };

  return (
    <AuthContext.Provider value={{
      session, user, profile, workspaces, currentWorkspaceId, setCurrentWorkspaceId,
      isLoading, signUp, signIn, signOut, hasRole,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
